import React from 'react';
import { useDispatch } from 'react-redux';
import { StyledSectionContainer } from '../styles';
import {
  StyledFileTypeDescription,
  StyledFileTypesBlock,
  StyledFileTypesComponentContainer,
  StyledFileTypesContainer,
} from './styles';
import fileTypes, { FileType, componentFileTypesOnly } from './fileTypes';
import Component from './Component';
import thunks from '../../../../../store/components/thunks';
import { displayNotification } from '../../../../../store/notification/actions';
import { LocalSection, Section } from '../../../../../service/components/types';
import { FileTypeName } from '@cambridgeassessment/lib-ee-commons';
import DocumentFileType from './DocumentFileType';

interface Props {
  section: Section | LocalSection;
  children: React.ReactNode;
  disabled?: boolean;
  componentId: string;
  componentName: string;
}

const Container = ({ section, children, disabled, componentId, componentName }: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const isPlagiarismChecked = !!section.plagiarismAndCollusion;
  const isDocumentChecked = !!section.fileTypes?.find((item: FileType) => item.name === FileTypeName.DOCUMENT);
  const isWordDocumentChecked = !!section.fileTypes?.find(
    (item: FileType) => item.name === FileTypeName.WORD_DOCUMENT_ONLY,
  );
  const document = FileTypeName.DOCUMENT;
  const wordDocument = FileTypeName.WORD_DOCUMENT_ONLY;

  const updateLearnerFileTypes = (updatedSection: Section): void => {
    dispatch(thunks.updateSection(componentId, updatedSection, 'fileTypes', updatedSection.id));
  };

  const onFileTypeChange = (name: string, value: boolean): void => {
    const sectionToUpdate = { ...section, fileTypes: section.fileTypes || [] };
    if (value) {
      const fileTypeToUpdate = fileTypes.find((item: FileType) => item.name === name);

      if (fileTypeToUpdate != null) {
        sectionToUpdate.fileTypes.push(fileTypeToUpdate);
      }

      if (name === wordDocument && isDocumentChecked) {
        const fileTypeToRemove = sectionToUpdate.fileTypes.findIndex((item) => item.name === document);
        sectionToUpdate.fileTypes.splice(fileTypeToRemove, 1);
      }
      if (name === document && isWordDocumentChecked) {
        const fileTypeToRemove = sectionToUpdate.fileTypes.findIndex((item) => item.name === wordDocument);
        sectionToUpdate.fileTypes.splice(fileTypeToRemove, 1);
      }
    } else {
      const fileTypeToRemove = sectionToUpdate.fileTypes.findIndex((item) => item.name === name);
      sectionToUpdate.fileTypes.splice(fileTypeToRemove, 1);
    }
    updateLearnerFileTypes(sectionToUpdate as Section);
  };

  const showSnackbarForFileTypes = (
    isTheLastChecked: boolean,
    fileType?: FileTypeName,
    isWordDocTheLastOneStanding?: boolean,
  ): void => {
    if (!disabled) {
      if (
        (isTheLastChecked && fileType !== wordDocument) ||
        (fileType === wordDocument && isWordDocTheLastOneStanding)
      ) {
        dispatch(displayNotification('At least one option must be present at all times'));
      }

      if (isPlagiarismChecked) {
        dispatch(
          displayNotification(
            'You cannot add further file types for this component section until the plagiarism and collusion checkbox is unticked. This needs to be actioned by a Compliance Officer.',
          ),
        );
      }
      if (
        ((fileType === wordDocument && isDocumentChecked) || (fileType === document && isWordDocumentChecked)) &&
        !isPlagiarismChecked
      ) {
        dispatch(
          displayNotification(
            'You cannot select both a ‘Document’ file format and a ‘Word Document Only’ file format. Please select one or the other.',
          ),
        );
      }
    }
  };

  const fileTypesDescription =
    'These types of files can be uploaded by the Exams Officer. At least one option must be present at all times';

  const renderFileType = (fileType: FileType, index: number): React.ReactElement => {
    const isChecked = !!section.fileTypes?.find((item: FileType) => item.name === fileType.name);
    const isTheLastChecked = (section.fileTypes?.length as number) <= 1 && isChecked;
    const onlyWordDocChecked = (section.fileTypes?.length as number) <= 1 && isWordDocumentChecked;
    let subtext: string | undefined;
    if (fileType.name === FileTypeName.COMPRESSION) {
      subtext = '(Not supported by RMA3)';
    }

    const nonDocumentFileType =
      fileType.name !== FileTypeName.DOCUMENT && fileType.name !== FileTypeName.WORD_DOCUMENT_ONLY;

    if (nonDocumentFileType) {
      return (
        <Component
          fileType={fileType}
          checked={isChecked}
          disabled={disabled || isPlagiarismChecked || isTheLastChecked}
          onChange={onFileTypeChange}
          isTheLastChecked={isTheLastChecked}
          showSnackbar={showSnackbarForFileTypes}
          dataQaId={`${componentName}-${section.name}`}
          key={`${fileType.name}-${index}`}
          subtext={subtext}
        />
      );
    }

    return (
      <DocumentFileType
        componentName={componentName}
        componentId={componentId}
        section={section}
        fileType={fileType}
        disabled={disabled}
        showSnackbar={showSnackbarForFileTypes}
        onChange={onFileTypeChange}
        isTheLastChecked={isTheLastChecked}
        onlyWordDocChecked={onlyWordDocChecked}
        dataQaId={`${componentName}-${section.name}`}
        key={`${fileType.name}-${index}`}
        isDocumentChecked={isDocumentChecked}
        isWordDocumentChecked={isWordDocumentChecked}
      />
    );
  };

  return (
    <StyledFileTypesComponentContainer>
      <StyledSectionContainer>File types</StyledSectionContainer>
      <StyledFileTypesBlock disabled={disabled}>
        <StyledFileTypeDescription>{fileTypesDescription}</StyledFileTypeDescription>
        <StyledFileTypesContainer>{componentFileTypesOnly.map(renderFileType)}</StyledFileTypesContainer>
      </StyledFileTypesBlock>
      {children}
    </StyledFileTypesComponentContainer>
  );
};

export default Container;
